<template>
  <div class="pre-survey-form-container">
    <div v-if="isLoadingPublicDetails === true" class="survey-loading-state">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p>Carregando pesquisa...</p>
    </div>

    <div v-else class="pre-survey-form-content">
      <h1 class="survey-title">{{ surveyPublicDetails.titleForConsumer }}</h1>
      <p class="survey-description">{{ surveyPublicDetails.descriptionForConsumer }}</p>

      <v-card elevation="0" class="survey-form-card">
        <v-form v-if="requiresUserInput" ref="form" class="survey-form" lazy-validation :disabled="isSubmittingForm" @submit.prevent="submitForm">
          <div v-if="showCpfInput" class="survey-form-field-item">
            <span class="survey-form-label">Digite seu CPF ou CNPJ</span>
            <v-text-field v-model="cpfCnpj" :rules="cpfCnpjRules" :readonly="isDocumentReadonly" outlined name="cpfCnpj"></v-text-field>
          </div>

          <div v-if="showUnitSelector" class="survey-form-field-item">
            <span class="survey-form-label">Selecione a loja</span>
            <v-autocomplete
              ref="unitAutocomplete"
              v-model="selectedUnit"
              :items="unitItems"
              :rules="unitRules"
              :loading="isLoadingFavoriteUnit"
              :hint="favoriteUnitHint"
              :persistent-hint="isLoadingFavoriteUnit"
              :disabled="!isUnitSearchEnabled"
              :filter="filterUnitItems"
              :menu-props="unitAutocompleteMenuProps"
              item-text="name"
              item-value="id_erp"
              attach
              outlined
              clearable
              no-data-text="Nenhuma loja encontrada"
              placeholder="Busque pelo nome ou endereço da loja"
              name="unit"
            >
              <template #item="{ item, on, attrs }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-content class="unit-option-content">
                    <v-list-item-title class="unit-option-title">{{ item.name }}</v-list-item-title>
                    <div v-if="getUnitAddress(item)" class="unit-option-subtitle">
                      {{ getUnitAddress(item) }}
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </div>
        </v-form>

        <p v-if="!requiresUserInput && hasDefaultSlotContent" class="validation-text">Validando navegador...</p>
        <div v-if="showTurnstileInForm">
          <slot></slot>
        </div>

        <v-btn
          v-if="requiresUserInput"
          class="survey-form-button"
          elevation="1"
          :color="assets.colors.form.buttonBackground"
          :style="{ color: assets.colors.form.buttonFont }"
          :loading="isSubmittingForm || isValidatingForm || isLoadingFavoriteUnit"
          :disabled="isSubmitDisabled"
          @click="submitForm"
        >
          Ir para pesquisa
          <v-icon right dark> mdi-arrow-right </v-icon>
        </v-btn>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pre-survey-form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 70px;
  height: calc(100vh - 80px);
  width: 100%;
  background-color: #f6f6f8;

  @media screen and (max-width: 768px) {
    padding-top: 0;
    height: 100%;
  }
}

.pre-survey-form-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 20px;
  }
}

.survey-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
}

.survey-title {
  font-size: 36px;
  font-weight: Bold;
  color: #4b525a;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 24px;
  }
}

.survey-description {
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
  margin-bottom: 48px;
  text-align: center;
}

.survey-form-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 70vw;
  padding: 40px;
  border-radius: 8px;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
}

.survey-form {
  display: flex;
  flex-direction: row;
  min-width: 300px;
  max-width: 100%;
  gap: 80px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
}

.survey-form-field-item {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
}

.survey-form-label {
  font-size: 16px;
  font-weight: 500;
  color: #4b525a;
}

::v-deep .unit-autocomplete-menu {
  width: 100% !important;
  overflow-x: hidden;
}

.unit-option-content {
  padding: 8px 0;
  min-width: 0;
  overflow: hidden;
}

.unit-option-title {
  font-size: 16px;
  font-weight: 400;
  color: #4b525a;
  line-height: 1.25;
  white-space: normal;
}

.unit-option-subtitle {
  font-size: 14px;
  font-weight: 300;
  color: #9ca3af;
  line-height: 1.3;
  margin-top: 2px;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.survey-form-button {
  min-width: 300px !important;
}

.validation-text {
  font-size: 1.25rem;
  font-weight: 500;
  color: #4b525a;
  text-align: center;
  margin-bottom: 16px;
}
</style>

<script>
import { debounce } from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { validateCpfCnpj } from '@/config/custom-validators'
import { formatCpfOrCnpj } from '@/helpers/cpfCnpj'
import { sanitizeCnpj } from '@/helpers/cnpj'
import { diacriticSpaceSensitiveRegex } from '@/helpers/search'
import surveyEntryMixin from '@/modules/survey/mixins/survey-entry-mixin'

const FAVORITE_UNIT_DEBOUNCE_MS = 400

export default {
  name: 'PreSurveyForm',
  mixins: [surveyEntryMixin],
  props: {
    surveyId: {
      type: String,
      required: true
    },
    usingShortcode: {
      type: Boolean,
      default: false
    },
    isSubmittingForm: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isLoadingPublicDetails: true,
    cpfCnpj: '',
    cpfCnpjRules: [v => !!v || 'CPF ou CNPJ é obrigatório', v => (validateCpfCnpj(v) ? true : 'CPF ou CNPJ inválido')],
    selectedUnit: null,
    unitRules: [v => !!v || 'Loja é obrigatória'],
    unitIdFromQuery: false,
    isValidatingForm: false,
    isLoadingFavoriteUnit: false,
    isUnitSearchEnabled: false,
    favoriteUnitRequestId: 0,
    unitAutocompleteMenuProps: {
      offsetY: true,
      contentClass: 'unit-autocomplete-menu'
    }
  }),
  computed: {
    ...mapGetters(['GET_ASSETS']),
    ...mapGetters('survey', ['GET_SURVEY_PUBLIC_DETAILS', 'GET_SURVEY_USER_ID', 'GET_SURVEY_FAVORITE_UNIT_ID']),
    assets() {
      return this.GET_ASSETS
    },
    surveyPublicDetails() {
      return this.GET_SURVEY_PUBLIC_DETAILS || {}
    },
    surveyRequiresUnitSelection() {
      return this.surveyPublicDetails?.units?.length > 0
    },
    showUnitSelector() {
      return this.surveyRequiresUnitSelection && !this.unitIdFromQuery
    },
    showCpfInput() {
      return !this.usingShortcode && (this.surveyPublicDetails?.visibility === 'PRIVATE' || this.surveyPublicDetails?.visibility === 'SEGMENTED')
    },
    hasPrefilledDocument() {
      return this.usingShortcode && !!this.GET_SURVEY_USER_ID
    },
    isDocumentReadonly() {
      return this.hasPrefilledDocument
    },
    requiresUserInput() {
      return this.showCpfInput || this.showUnitSelector
    },
    hasDefaultSlotContent() {
      const slot = this.$slots.default
      return Array.isArray(slot) && slot.length > 0
    },
    unitItems() {
      return this.surveyPublicDetails.units || []
    },
    showTurnstileInForm() {
      if (!this.hasDefaultSlotContent) return false
      if (!this.requiresUserInput) return true
      return this.showUnitSelector ? this.isUnitSearchEnabled : true
    },
    isDocumentValid() {
      if (!this.showCpfInput) return true
      return !!this.cpfCnpj && validateCpfCnpj(this.cpfCnpj)
    },
    favoriteUnitHint() {
      return this.isLoadingFavoriteUnit ? 'Identificando loja favorita...' : ''
    },
    isSubmitDisabled() {
      if (this.isLoadingFavoriteUnit) return true
      if (this.showCpfInput && !this.isDocumentValid) return true
      if (this.showUnitSelector && !this.isUnitSearchEnabled) return true
      if (this.showUnitSelector && !this.selectedUnit) return true
      return false
    }
  },
  watch: {
    cpfCnpj(value) {
      const formatted = formatCpfOrCnpj(value)
      if (formatted !== value) {
        this.$nextTick(() => {
          this.cpfCnpj = formatted
        })
        return
      }

      if (this.showCpfInput && this.showUnitSelector && !this.hasPrefilledDocument) {
        this.resetUnitFieldValidation()
        this.onDocumentChangeDebounced()
      }
    },
    selectedUnit(value) {
      if (!value) {
        this.resetUnitFieldValidation()
      }
    }
  },
  created() {
    this.onDocumentChangeDebounced = debounce(this.onDocumentChange, FAVORITE_UNIT_DEBOUNCE_MS)
  },
  async mounted() {
    try {
      await this.fetchSurveyPublicDetails(this.surveyId)

      const { unitId } = this.$route.query
      if (unitId) {
        this.unitIdFromQuery = true
        this.selectedUnit = unitId
        this.isUnitSearchEnabled = true
      }

      if (this.hasPrefilledDocument) {
        this.cpfCnpj = formatCpfOrCnpj(this.GET_SURVEY_USER_ID)
        this.setSurveyUserId(this.GET_SURVEY_USER_ID)
      }

      if (!this.requiresUserInput) {
        this.setSurveySelectedUnit(this.selectedUnit)
        this.$emit('automatic-submit')
        return
      }

      if (this.showUnitSelector && !this.unitIdFromQuery) {
        if (this.hasPrefilledDocument) {
          this.applyPrefilledFavoriteUnit()
        } else if (this.showCpfInput) {
          this.isUnitSearchEnabled = false
        } else {
          this.isUnitSearchEnabled = true
        }
      } else if (!this.showUnitSelector) {
        this.isUnitSearchEnabled = true
      }
    } catch (error) {
      this.redirectToError(error)
    } finally {
      this.isLoadingPublicDetails = false
    }
  },
  beforeDestroy() {
    if (this.onDocumentChangeDebounced) {
      this.onDocumentChangeDebounced.cancel()
    }
  },
  methods: {
    ...mapActions('survey', ['fetchSurveyPublicDetails', 'fetchConsumerFavoriteUnit', 'setSurveySelectedUnit', 'setSurveyUserId']),

    getUnitAddress(unit) {
      return unit?.address || unit?.address_components || ''
    },

    filterUnitItems(item, queryText) {
      if (!queryText) return true

      const searchRegex = diacriticSpaceSensitiveRegex(queryText)
      const address = this.getUnitAddress(item)
      return searchRegex.test(item.name || '') || (address && searchRegex.test(address))
    },

    getUserIdForFavoriteUnit() {
      return sanitizeCnpj(this.cpfCnpj)
    },

    applyPrefilledFavoriteUnit() {
      this.applyFavoriteUnitId(this.GET_SURVEY_FAVORITE_UNIT_ID)
    },

    applyFavoriteUnitId(favoriteUnitId) {
      if (favoriteUnitId && this.unitItems.some(unit => unit.id_erp === favoriteUnitId)) {
        this.selectedUnit = favoriteUnitId
      }
      this.isUnitSearchEnabled = true
    },

    onDocumentChange() {
      if (!this.showCpfInput || !this.showUnitSelector || this.hasPrefilledDocument) return

      if (!this.isDocumentValid) {
        this.resetUnitSelection()
        this.isUnitSearchEnabled = false
        return
      }

      this.resolveFavoriteUnit(this.getUserIdForFavoriteUnit())
    },

    resetUnitFieldValidation() {
      this.$nextTick(() => {
        const unitField = this.$refs.unitAutocomplete
        if (unitField) {
          unitField.resetValidation()
        }
      })
    },

    resetUnitSelection() {
      this.selectedUnit = null
      this.isUnitSearchEnabled = false
    },

    async resolveFavoriteUnit(userId) {
      if (!userId || !this.showUnitSelector) {
        this.isUnitSearchEnabled = true
        return
      }

      const requestId = ++this.favoriteUnitRequestId
      this.isLoadingFavoriteUnit = true
      this.isUnitSearchEnabled = false
      this.selectedUnit = null

      try {
        const favoriteUnitId = await this.fetchConsumerFavoriteUnit({
          surveyId: this.surveyId,
          userId
        })

        if (requestId !== this.favoriteUnitRequestId) return

        this.applyFavoriteUnitId(favoriteUnitId)
      } catch (error) {
        if (requestId === this.favoriteUnitRequestId) {
          console.error('Error resolving favorite unit:', error)
        }
      } finally {
        if (requestId === this.favoriteUnitRequestId) {
          this.isLoadingFavoriteUnit = false
          this.isUnitSearchEnabled = true
        }
      }
    },

    async submitForm() {
      this.isValidatingForm = true

      if (!this.$refs.form.validate()) {
        this.$snackbar({
          message: 'Preencha o formulário corretamente.',
          snackbarColor: 'error'
        })
        this.isValidatingForm = false
        return
      }

      if (this.showCpfInput) {
        this.setSurveyUserId(sanitizeCnpj(this.cpfCnpj))
      }
      this.setSurveySelectedUnit(this.selectedUnit)
      this.$emit('submit')
      this.isValidatingForm = false
    }
  }
}
</script>
